import { discoverEmberDataModels } from 'ember-cli-mirage';
import { createServer, Response } from 'miragejs';
import config from 'garaje/config/environment';
import { faker } from '@faker-js/faker';
import timekeeper from 'timekeeper';
import seedrandom from 'seedrandom';

import authRoutes from './routes/auth';
import authzRoutes from './routes/authz';
import billingRoutes from './routes/billing';
import deliveriesRoutes from './routes/deliveries';
import hooksRoutes from './routes/hooks';
import hubRoutes from './routes/hub';
import roomsRoutes from './routes/rooms';
import rmsRoutes from './routes/rms';
import visitorsRoutes from './routes/visitors';
import multiTenancyRoutes from './routes/multi-tenancy';
import roombaRoutes from './routes/roomba';
import growthRoutes from './routes/growth';
import chiliPiperRoutes from './routes/chili-piper';
import mapsRoutes from './routes/maps';
import federatedGraphqlRoutes from './routes/federated_graphql';
import basicGraphqlRoutes from './routes/basic_graphql';
import slackPluginRoutes from './routes/slack-plugin';
import teamsPluginRoutes from './routes/teams-plugin';
import accessEventRoutes from './routes/access-event';

if (config.isCypress) {
  // travel back in time. important that we don't _freeze_ so that timers work as
  // normal.
  const now = new Date(Date.UTC(2021, 0, 11));
  timekeeper.travel(now);
  // seed Math.random
  seedrandom('420', { global: true });
  // seed Faker
  faker.seed(420);
}

/**
 * Configures mirage for development
 *
 * @param {object} server Mirage Server
 */
function devMode(server) {
  server.passthrough('/assets/**');

  for (let i = 1; i <= 20; i++) {
    server.passthrough(`https://ps${i}.pubnub.com/**`);
  }

  if (config.bugsnagConfig.apiKey) {
    server.passthrough('https://sessions.bugsnag.com');
    server.passthrough('https://notify.bugsnag.com');
  }

  server.passthrough('https://api.segment.io/**');
  server.passthrough('https://api.stream-io-api.com/**');
}

/**
 * Initializes Mirage
 */
export default function (config) {
  const finalConfig = {
    ...config,
    models: {
      ...discoverEmberDataModels(config.store),
      ...config.models,
    },
    routes,
  };

  return createServer(finalConfig);
}

function routes() {
  if (config.isCypress) {
    // by default mirage injects a delay of up to 400ms
    this.timing = 0;
  }

  if (config.mirageEnabled) {
    devMode(this);
  }

  if (config.environment === 'development' && ['ui-hooks', 'ui-hooks-badge'].includes(config.MIRAGE_SCENARIO)) {
    hooksRoutes.call(this);
    this.passthrough();

    return;
  }

  this.get('https://maps.googleapis.com/maps/api/timezone/json', function () {
    return new Response(
      200,
      {},
      {
        dstOffset: 3600,
        rawOffset: -28800,
        status: 'OK',
        timeZoneId: 'America/Los_Angeles',
        timeZoneName: 'Pacific Daylight Time',
      },
    );
  });

  authRoutes.call(this);
  authzRoutes.call(this);
  billingRoutes.call(this);
  deliveriesRoutes.call(this);
  hooksRoutes.call(this);
  hubRoutes.call(this);
  multiTenancyRoutes.call(this);
  rmsRoutes.call(this);
  roombaRoutes.call(this);
  mapsRoutes.call(this);
  roomsRoutes.call(this);
  visitorsRoutes.call(this);
  growthRoutes.call(this);
  federatedGraphqlRoutes.call(this);
  basicGraphqlRoutes.call(this);
  chiliPiperRoutes.call(this);
  slackPluginRoutes.call(this);
  teamsPluginRoutes.call(this);
  accessEventRoutes.call(this);
}
